// FeeRequest.jsx
// Full responsive refactor — full functionality preserved.
// Keep this file as `FeeRequest.jsx` (or replace your existing component file).

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  IconButton,
  Modal,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  CircularProgress,
  Stack,
  FormHelperText,
  FormControl,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../Components/Snackbar/SnackbarContext";

const truncate = (text, len = 18) => {
  if (!text) return "";
  return text.length > len ? text.substring(0, len) + "..." : text;
};

const FeeRequest = ({ feeItems = [] }) => {
  const { showSnackbar } = useSnackbar();

  // main lists
  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);

  // filters
  const [branch, setBranch] = useState("");
  const [classGuid, setClassGuid] = useState("");

  // requests and students
  const [feeRequests, setFeeRequests] = useState([]);
  const [students, setStudents] = useState([]);

  // loading
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [feeType, setFeeType] = useState("standard");
  const [feeId, setFeeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // view modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentRequests, setStudentRequests] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  const token = localStorage.getItem("token");
  const [formErrors, setFormErrors] = useState({
    fromDate: "",
    toDate: "",
    feeType: "",
    feeId: "",
    dueDate: "",
  });

  const [filterErrors, setFilterErrors] = useState({
    branch: "",
    classGuid: "",
  });

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    // whenever branch changes reset dependent values
    if (!branch) {
      setClassList([]);
      setClassGuid("");
    }
  }, [branch]);

  useEffect(() => {
    if (branch && classGuid) loadFeeRequests();
  }, [branch, classGuid]);

  const loadBranches = async () => {
    try {
      setLoadingBranches(true);
      const res = await axios.get(
        `${apiEndpoints.dropdown}?type=branch&mode=initial`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setBranchList(res.data.data || []);
    } catch (err) {
      showSnackbar("Failed to load branches", "error");
    } finally {
      setLoadingBranches(false);
    }
  };

  const loadClasses = async (branchGuid) => {
    try {
      const res = await axios.get(
        `${apiEndpoints.dropdown}?type=class&branch_guid=${branchGuid}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setClassList(res.data.data || []);
    } catch (err) {
      showSnackbar("Failed to load classes", "error");
    }
  };

  const loadFeeRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiEndpoints.fee_request}?action=get_requests&branch_guid=${branch}&class_guid=${classGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeeRequests(res.data.data || []);
    } catch (err) {
      showSnackbar("Failed to load fee requests", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    if (!branch || !classGuid) {
      showSnackbar("Please select both branch and class", "warning");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${apiEndpoints.fee_request}?action=filter_students&branch_guid=${branch}&class_guid=${classGuid}&from=${fromDate}&to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const list = (res.data.data || []).map((s) => ({
        ...s,
        guid: String(s.guid),
        class_guid: s.class_guid ? String(s.class_guid) : s.class_guid,
      }));

      setStudents(list);
      setSelectedStudents([]);
    } catch (err) {
      showSnackbar("Failed to fetch students", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitFeeRequest = async () => {
    const errors = {};

    // if (!fromDate) errors.fromDate = "From date is required";
    // if (!toDate) errors.toDate = "To date is required";

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      errors.toDate = "End date cannot be before start date";
    }

    if (!feeType) errors.feeType = "Select fee type";
    if (!feeId) errors.feeId = "Select a fee item";
    if (!dueDate) errors.dueDate = "Select a due date";

    if (selectedStudents.length === 0) {
      showSnackbar("Select at least one student", "error");
      return;
    }
const today = new Date().toISOString().split("T")[0];

if (dueDate < today) {
  setFormErrors((prev) => ({
    ...prev,
    dueDate: "Due date cannot be in the past",
  }));
  return;
}

    // If errors exist → stop submission
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Clear errors
    setFormErrors({});
    try {
      const payload = {
        students: selectedStudents,
        fee_id: feeId,
        is_custom_fee: feeType === "custom" ? 1 : 0,
        dueDate,
      };

      await axios.post(
        `${apiEndpoints.fee_request}?action=create_requests`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSnackbar("Fee request created successfully", "success");

      // reset form
      setShowAddForm(false);
      setSelectedStudents([]);
      setFeeId("");
      setDueDate("");

      // refresh data
      loadFeeRequests();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create requests";
      showSnackbar(msg, "error");
    }
  };

  const viewStudentRequests = async (student) => {
    setSelectedStudent(student);
    setStudentRequests([]);
    try {
      setLoadingModal(true);
      const res = await axios.get(
        `${apiEndpoints.fee_request}?action=student_requests&student_guid=${student.guid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudentRequests(res.data.data || []);
      setViewModalOpen(true);
    } catch (err) {
      showSnackbar("Failed to load student requests", "error");
    } finally {
      setLoadingModal(false);
    }
  };

  const deleteStudentRequest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?"))
      return;

    try {
      await axios.post(
        `${apiEndpoints.fee_request}?action=delete_request`,
        { id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSnackbar("Request deleted successfully", "success");

      // refresh modal and list
      if (selectedStudent) viewStudentRequests(selectedStudent);
      loadFeeRequests();
    } catch (err) {
      showSnackbar("Failed to delete request", "error");
    }
  };

  const toggleStudent = (guid) => {
    const id = String(guid);
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (checked) => {
    if (checked) setSelectedStudents(students.map((s) => String(s.guid)));
    else setSelectedStudents([]);
  };

  const selectedCount = selectedStudents.length;

  return (
    <>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mb: 3,
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Fee Requests
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm((s) => !s)}
              sx={{ borderRadius: 999, px: 3 }}
            >
              {showAddForm ? "Cancel" : "Add Request"}
            </Button>
          </Stack>
        </Box>

        {/* filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth error={Boolean(filterErrors.branch)}>
              <TextField
                select
                label="Branch"
                fullWidth
                value={branch}
                onChange={(e) => {
                  setBranch(e.target.value);
                  setClassGuid("");
                  loadClasses(e.target.value);
                  setFilterErrors({ ...filterErrors, branch: "" });
                }}
                InputProps={{ sx: { borderRadius: 3 } }}
              >
                <MenuItem value="">Select branch</MenuItem>
                {branchList.map((b) => (
                  <MenuItem key={b.guid} value={b.guid}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>

              <FormHelperText>{filterErrors.branch}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth error={Boolean(filterErrors.classGuid)}>
              <TextField
                select
                label="Class"
                fullWidth
                value={classGuid}
                disabled={!branch}
                onChange={(e) => {
                  setClassGuid(e.target.value);
                  setFilterErrors({ ...filterErrors, classGuid: "" });
                }}
                InputProps={{ sx: { borderRadius: 3 } }}
              >
                <MenuItem value="">Select class</MenuItem>
                {classList.map((c) => (
                  <MenuItem key={c.guid} value={c.guid}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>

              <FormHelperText>{filterErrors.classGuid}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                let errors = {};

                if (!branch) errors.branch = "Please select a branch";
                if (!classGuid) errors.classGuid = "Please select a class";

                setFilterErrors(errors);

                if (Object.keys(errors).length === 0) {
                  loadFeeRequests();
                }
              }}
              disabled={!branch || !classGuid || loading}
              sx={{
                borderRadius: 999,
                px: 3,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {loading ? <CircularProgress size={18} /> : "Refresh"}
            </Button>
          </Grid>
        </Grid>

        {/* Add Request Form */}
        {showAddForm && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: "rgba(255,255,255,0.55)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Add New Fee Request
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="From"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setFormErrors({ ...formErrors, fromDate: "" });
                  }}
                  // error={Boolean(formErrors.fromDate)}
                  // helperText={formErrors.fromDate}
                  InputProps={{ sx: { borderRadius: 3 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="To"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setFormErrors({ ...formErrors, toDate: "" });
                  }}
                  // error={Boolean(formErrors.toDate)}
                  // helperText={formErrors.toDate}
                  InputProps={{ sx: { borderRadius: 3 } }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={fetchStudents}
                  sx={{ borderRadius: 999 }}
                >
                  Load Students
                </Button>
              </Grid>

              <Grid item xs={12} md={3} />
            </Grid>

            {/* students grid */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              students.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Checkbox
                      checked={
                        students.length > 0 &&
                        selectedStudents.length === students.length
                      }
                      indeterminate={
                        selectedStudents.length > 0 &&
                        selectedStudents.length < students.length
                      }
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                    />
                    <Typography>
                      Select All ({selectedCount} / {students.length} selected)
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {students.map((stu) => {
                      const id = String(stu.guid);
                      const checked = selectedStudents.includes(id);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
                          <Paper
                            onClick={() => toggleStudent(stu.guid)}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              background: checked
                                ? "rgba(33,150,243,0.06)"
                                : "transparent",
                              border: checked
                                ? "2px solid rgba(33,150,243,0.16)"
                                : "1px solid rgba(0,0,0,0.06)",
                              cursor: "pointer",
                              transition: "all 0.18s",
                              "&:hover": { boxShadow: 2 },
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleStudent(stu.guid)}
                            />

                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                fontWeight={600}
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 220,
                                }}
                              >
                                {stu.first_name} {stu.last_name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 220,
                                }}
                              >
                                {stu.class_name || "-"}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )
            )}

            <Grid container spacing={3} alignItems="center" sx={{ mt: 1 }}>
              {/* Fee Type */}
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Fee Type"
                  size="small"
                  value={feeType}
                  onChange={(e) => {
                    setFeeType(e.target.value);
                    setFeeId("");
                    setFormErrors({ ...formErrors, feeType: "" });
                  }}
                  error={Boolean(formErrors.feeType)}
                  helperText={formErrors.feeType}
                  InputProps={{ sx: { borderRadius: 3 } }}
                >
                  <MenuItem value="standard">Standard Fee</MenuItem>
                  <MenuItem value="custom">Custom Fee</MenuItem>
                </TextField>
              </Grid>

              {/* Fee Item */}
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Fee Item"
                  size="small"
                  value={feeId}
                  onChange={(e) => {
                    setFeeId(e.target.value);
                    setFormErrors({ ...formErrors, feeId: "" });
                  }}
                  disabled={selectedCount === 0}
                  error={Boolean(formErrors.feeId)}
                  helperText={formErrors.feeId}
                  InputProps={{ sx: { borderRadius: 3 } }}
                >
                  <MenuItem value="">Select fee</MenuItem>

                  {feeItems
                    .filter((f) => f.type === feeType)
                    .map((f) => (
                      <MenuItem
                        key={String(f.id)}
                        value={f.id}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {f.label} — ₹{f.amount}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {/* Due Date */}
              <Grid item xs={12} md={4}>
                <TextField
                  type="date"
                  fullWidth
                  label="Due Date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={dueDate}
                  onChange={(e) => {
                    setDueDate(e.target.value);
                    setFormErrors({ ...formErrors, dueDate: "" });
                  }}
                  disabled={selectedCount === 0}
                  error={Boolean(formErrors.dueDate)}
                  helperText={formErrors.dueDate}
                  InputProps={{ sx: { borderRadius: 3 } }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0], // 🔥 block past dates
                  }}
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              sx={{ mt: 3, borderRadius: 999, px: 3 }}
              onClick={submitFeeRequest}
              disabled={selectedCount === 0 || !feeId || !dueDate}
            >
              Create Fee Request ({selectedCount} student
              {selectedCount !== 1 ? "s" : ""})
            </Button>
          </Paper>
        )}

        {/* Fee requests table or empty states */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {branch && classGuid && feeRequests.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Existing Fee Requests ({feeRequests.length})
                </Typography>

                <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(0,0,0,0.04)" }}>
                        <TableCell>Student</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Total Requests</TableCell>
                        <TableCell>Last Request Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feeRequests.map((stu) => (
                        <TableRow key={stu.student_guid} hover>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {stu.first_name} {stu.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{stu.class_name}</TableCell>
                          <TableCell>{stu.total_requests}</TableCell>
                          <TableCell>
                            {stu.last_request_date
                              ? new Date(
                                  stu.last_request_date,
                                ).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                viewStudentRequests({
                                  guid: stu.student_guid,
                                  first_name: stu.first_name,
                                  last_name: stu.last_name,
                                  class_name: stu.class_name,
                                })
                              }
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              branch &&
              classGuid && (
                <Typography
                  sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
                >
                  No fee requests found for the selected class. Click "Add New
                  Request" to create one.
                </Typography>
              )
            )}

            {(!branch || !classGuid) && (
              <Typography
                sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
              >
                Select branch and class to view fee requests
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* VIEW STUDENT REQUESTS MODAL */}
      <Modal open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {selectedStudent?.first_name} {selectedStudent?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Class: {selectedStudent?.class_name} | Total Requests:{" "}
                {studentRequests.length}
              </Typography>
            </Box>
            <IconButton onClick={() => setViewModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loadingModal ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {studentRequests.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(0,0,0,0.04)" }}>
                        <TableCell>Fee Item</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentRequests.map((req) => (
                        <TableRow key={req.id} hover>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {req.fee_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={req.is_custom_fee ? "Custom" : "Standard"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">₹{req.amount}</TableCell>
                          <TableCell>
                            {new Date(req.due_date).toLocaleDateString()}{" "}
                          </TableCell>
                          <TableCell>
                            {new Date(req.created_at).toLocaleDateString()}{" "}
                          </TableCell>
                          <TableCell>{getStatusChip(req.status)}</TableCell>
                          <TableCell>
                            {req.payment_date
                              ? new Date(req.payment_date).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => deleteStudentRequest(req.id)}
                            >
                              <CloseIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                >
                  No fee requests found for this student
                </Typography>
              )}

              {/* summary */}
              {studentRequests.length > 0 && (
                <Box
                  sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Typography variant="h6" color="primary">
                            {studentRequests.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Requests
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Typography variant="h6" color="success.main">
                            {
                              studentRequests.filter((r) => r.status === "paid")
                                .length
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Paid
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Typography variant="h6" color="warning.main">
                            {
                              studentRequests.filter(
                                (r) => r.status === "pending",
                              ).length
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pending
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Typography variant="h6">
                            ₹
                            {studentRequests.reduce(
                              (sum, req) => sum + parseFloat(req.amount || 0),
                              0,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Amount
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>
      </Modal>
    </>
  );

  function getStatusChip(status) {
    const statusConfig = {
      pending: { color: "warning", label: "Pending" },
      paid: { color: "success", label: "Paid" },
      overdue: { color: "error", label: "Overdue" },
      cancelled: { color: "default", label: "Cancelled" },
    };
    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  }
};

export default FeeRequest;
